import * as React from "react"

import { ApiError } from "./contracts"

/**
 * A minimal `useMutation` implementing the subset of React Query v5's
 * public surface this feature relies on. Feature hooks build on top so
 * their call sites stay unchanged when the app graduates to full RQ.
 *
 * Lifecycle:
 *   1. `onMutate(vars)` runs first and may return a context object.
 *      Optimistic writes belong here.
 *   2. `mutationFn(vars, signal)` performs the network call. It must
 *      honour `signal` — aborted calls surface as an `ApiError("network")`
 *      and are handed to `onError` just like any other failure.
 *   3. On resolution, `onSuccess(vars)` runs. On rejection, `onError`
 *      receives the error alongside the context from step 1; rollback
 *      logic lives here.
 */

export interface MutationOptions<TVars, TCtx> {
  mutationFn: (vars: TVars, signal: AbortSignal) => Promise<void>
  /** Runs before `mutationFn`. Return value is threaded to `onError`. */
  onMutate?: (vars: TVars) => TCtx | void
  onError?: (error: ApiError, vars: TVars, ctx: TCtx | undefined) => void
  onSuccess?: (vars: TVars) => void
}

export interface MutationResult<TVars> {
  mutate: (vars: TVars) => void
  mutateAsync: (vars: TVars) => Promise<void>
  isPending: boolean
  error: ApiError | null
  reset: () => void
}

export function useMutation<TVars, TCtx = void>(
  options: MutationOptions<TVars, TCtx>,
): MutationResult<TVars> {
  const [isPending, setIsPending] = React.useState(false)
  const [error, setError] = React.useState<ApiError | null>(null)

  const optionsRef = React.useRef(options)
  React.useLayoutEffect(() => {
    optionsRef.current = options
  })

  const mutateAsync = React.useCallback(async (vars: TVars): Promise<void> => {
    const controller = new AbortController()
    setIsPending(true)
    setError(null)
    let ctx: TCtx | void
    try {
      ctx = optionsRef.current.onMutate?.(vars)
      await optionsRef.current.mutationFn(vars, controller.signal)
      optionsRef.current.onSuccess?.(vars)
    } catch (cause) {
      const err =
        cause instanceof ApiError
          ? cause
          : cause instanceof DOMException && cause.name === "AbortError"
            ? new ApiError("network", "Request aborted", 0)
            : new ApiError(
                "network",
                cause instanceof Error ? cause.message : String(cause),
                0,
              )
      setError(err)
      optionsRef.current.onError?.(err, vars, ctx as TCtx | undefined)
      throw err
    } finally {
      setIsPending(false)
    }
  }, [])

  const mutate = React.useCallback(
    (vars: TVars) => {
      void mutateAsync(vars).catch(() => undefined)
    },
    [mutateAsync],
  )

  const reset = React.useCallback(() => setError(null), [])

  return { mutate, mutateAsync, isPending, error, reset }
}
